import ReactECharts from "echarts-for-react";
import { Box, Typography } from "@mui/material";
import React, { FC, useCallback, useMemo, useState } from "react";
import * as echarts from "echarts";

import { ILineChartData, IRpiLineChartData } from ".";

interface IVehicleCountChartProps {
	data: ILineChartData;
	loading?: boolean;
	rpiData?: IRpiLineChartData;
}

type VehicleCountChartType = "visitor_type" | "vehicle_type";

type ChartType = "default" | "rpi_visit_type" | "rpi_vehicle_type";

const LINE_CHART_COLORS = {
	ENTRY: "#01579B",
	EXIT: "#D32F2F",
	REGISTERED: "#388E3C",
	UNREGISTERED: "#FFAF00",
	VISITOR: "#388DFF",
	BIKE: "#FFAF00",
	CAR: "#388DFF"
};

const VehicleCountChart: FC<IVehicleCountChartProps> = (props) => {
	const { data, loading, rpiData } = props;

	const [selectedGraphType, setSelectedGraphType] = useState<VehicleCountChartType>("visitor_type");

	function getLineChartColors(type: ChartType) {
		if (type === "default") {
			return [LINE_CHART_COLORS.ENTRY, LINE_CHART_COLORS.EXIT];
		}

		if (type === "rpi_visit_type") {
			return [LINE_CHART_COLORS.REGISTERED, LINE_CHART_COLORS.UNREGISTERED, LINE_CHART_COLORS.VISITOR];
		}

		if (type === "rpi_vehicle_type") {
			return [LINE_CHART_COLORS.BIKE, LINE_CHART_COLORS.CAR];
		}

		return [];
	}

	function getLegendData(type: ChartType) {
		let dataArray: any[] = [];

		if (type === "default") {
			dataArray = [
				{ name: "Entry", color: LINE_CHART_COLORS.ENTRY },
				{ name: "Exit", color: LINE_CHART_COLORS.EXIT }
			];
		} else if (type === "rpi_visit_type") {
			dataArray = [
				{ name: "Authorized", color: LINE_CHART_COLORS.REGISTERED },
				{ name: "Unauthorized", color: LINE_CHART_COLORS.UNREGISTERED },
				{ name: "Visitor", color: LINE_CHART_COLORS.VISITOR }
			];
		} else if (type === "rpi_vehicle_type") {
			dataArray = [
				{ name: "2 Wheeler", color: LINE_CHART_COLORS.BIKE },
				{ name: "4 Wheeler", color: LINE_CHART_COLORS.CAR }
			];
		}

		return dataArray.map((item) => ({
			name: item.name,
			lineStyle: { color: item.color, width: 3 },
			itemStyle: { opacity: 0 }
		}));
	}

	const checkRpiChartAxisVisible = useCallback(
		(type: ChartType): boolean => {
			if (type === "rpi_visit_type") {
				return (
					!!rpiData && (rpiData.registered.length > 0 || rpiData.unregistered.length > 0 || rpiData.visitor.length > 0)
				);
			}

			if (type === "rpi_vehicle_type") {
				return !!rpiData && (rpiData.bikes.length > 0 || rpiData.cars.length > 0);
			}

			return data.entry.length > 0 || data.exit.length > 0;
		},
		[data, rpiData]
	);

	function getRGBFromHex(hex: string, opacity?: number) {
		const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
		hex = hex.replace(shorthandRegex, (_m, r, g, b) => {
			return r + r + g + g + b + b;
		});

		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

		let rgb = "";

		if (result) {
			rgb = `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
		}

		if (typeof opacity === "number") {
			return `rgba(${rgb},${opacity})`;
		}

		return `rgb(${rgb})`;
	}

	const getLineChartSeries = useCallback(
		(type: ChartType) => {
			let dataArray: any[] = [];

			if (type === "default") {
				dataArray = [
					{ name: "Entry", data: data.entry, color: LINE_CHART_COLORS.ENTRY },
					{ name: "Exit", data: data.exit, color: LINE_CHART_COLORS.EXIT }
				];
			} else if (type === "rpi_visit_type") {
				dataArray = [
					{ name: "Authorized", data: rpiData?.registered ?? [], color: LINE_CHART_COLORS.REGISTERED },
					{ name: "Unauthorized", data: rpiData?.unregistered ?? [], color: LINE_CHART_COLORS.UNREGISTERED },
					{ name: "Visitor", data: rpiData?.visitor ?? [], color: LINE_CHART_COLORS.VISITOR }
				];
			} else if (type === "rpi_vehicle_type") {
				dataArray = [
					{ name: "2 Wheeler", data: rpiData?.bikes ?? [], color: LINE_CHART_COLORS.BIKE },
					{ name: "4 Wheeler", data: rpiData?.cars ?? [], color: LINE_CHART_COLORS.CAR },
					{ name: "Visitor", data: [], color: LINE_CHART_COLORS.VISITOR }
				];
			}

			return dataArray.map((item) => ({
				name: item.name,
				data: item.data,
				type: "line",
				smooth: 0.5,
				symbol: "none",
				lineStyle: { color: item.color, width: 3 },
				areaStyle: {
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{ offset: 0, color: getRGBFromHex(item.color, 0.18) },
						{ offset: 1, color: getRGBFromHex(item.color, 0) }
					])
				}
			}));
		},
		[data, rpiData]
	);

	const lineChartConfig = useMemo(() => {
		let chartType: ChartType = "default";

		if (rpiData) {
			if (selectedGraphType === "visitor_type") chartType = "rpi_visit_type";
			else if (selectedGraphType === "vehicle_type") chartType = "rpi_vehicle_type";
		}

		const showAxes = checkRpiChartAxisVisible(chartType);

		return {
			showAxes: showAxes,
			option: {
				color: getLineChartColors(chartType),
				tooltip: { trigger: "axis" },
				grid: { left: 0, right: 0, bottom: 0, containLabel: true },
				legend: {
					itemGap: 20,
					right: "25px",
					textStyle: { padding: [0, 0, 0, 10] },
					data: getLegendData(chartType)
				},
				xAxis: {
					type: "category",
					boundaryGap: true,
					data: rpiData ? rpiData.timestamps : data.timestamps,
					axisTick: { alignWithLabel: true },
					axisLine: { show: showAxes }
				},
				yAxis: {
					type: "value",
					splitLine: { show: false },
					axisLine: { show: showAxes }
				},
				series: getLineChartSeries(chartType)
			}
		};
	}, [data.timestamps, checkRpiChartAxisVisible, getLineChartSeries, rpiData, selectedGraphType]);

	function handleChangeSelectedGraphType(updatedValue: VehicleCountChartType) {
		setSelectedGraphType(updatedValue);
	}

	return (
		<Box sx={{ position: "relative", paddingTop: { xs: "40px" } }}>
			{rpiData ? (
				<Box
					className="graph-selector"
					sx={{
						position: "absolute",
						top: { xs: "-0.5rem", sm: "-1.5rem", md: "-0.5rem" },
						left: 0,
						zIndex: 999,
						width: { xs: "100%", sm: "auto" },
						justifyContent: { xs: "center", sm: "flex-start" }
					}}
				>
					<Box
						className={`graph-selector-tab left ${selectedGraphType === "visitor_type" ? "active" : ""}`}
						onClick={() => handleChangeSelectedGraphType("visitor_type")}
					>
						<Typography variant="body2">Visitor Type</Typography>
					</Box>

					<Box
						className={`graph-selector-tab right ${selectedGraphType === "vehicle_type" ? "active" : ""}`}
						onClick={() => handleChangeSelectedGraphType("vehicle_type")}
					>
						<Typography variant="body2">Vehicle Type</Typography>
					</Box>
				</Box>
			) : null}

			<ReactECharts
				style={{
					minHeight: rpiData ? "375px" : "375px"
					// marginTop: rpiData ? "3rem" : "0px"
				}}
				showLoading={!lineChartConfig.showAxes && loading}
				option={lineChartConfig.option}
			/>

			{!loading && !lineChartConfig.showAxes ? (
				<Box className="line-chart-no-data-overlay">
					<Typography>No data</Typography>
				</Box>
			) : null}
		</Box>
	);
};

export default VehicleCountChart;
